import React, { useState } from 'react';
import { X, SlidersHorizontal, Check, ChevronRight } from 'lucide-react';

export interface FilterState {
  selectedType: string;
  selectedSubtype: string;
  minRating: string;
  selectedCity: string;
  hotelStar: string;
  selectedTags: string[];
  hotelPriceRange: [number, number];
  selectedCuisines: string[];
  selectedSignatureDish: string[];
  selectedFoodTags: string[];
  foodPriceRange: [number, number];
  bestTimeOfDay: string[];
  bestSeason: string[];
  difficulty: string;
  crowdLevel: string;
  typicalTimeSpent: string;
  selectedAttractionTags: string[];
}

interface TravelFilterUIProps {
  onFiltersChange: (filters: FilterState) => void;
  onClose: () => void;
  isOpen: boolean;
}

const TravelFilterUI: React.FC<TravelFilterUIProps> = ({ onFiltersChange, onClose, isOpen }) => {
  const [selectedType, setSelectedType] = useState('all');
  const [selectedSubtype, setSelectedSubtype] = useState('all');
  const [minRating, setMinRating] = useState('any');
  const [selectedCity, setSelectedCity] = useState('all');
  
  // Hotel filters
  const [hotelStar, setHotelStar] = useState('any');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [hotelPriceRange, setHotelPriceRange] = useState<[number, number]>([0, 500]);
  
  // Food filters
  const [selectedCuisines, setSelectedCuisines] = useState<string[]>([]);
  const [selectedSignatureDish, setSelectedSignatureDish] = useState<string[]>([]);
  const [selectedFoodTags, setSelectedFoodTags] = useState<string[]>([]);
  const [foodPriceRange, setFoodPriceRange] = useState<[number, number]>([0, 10000]);
  
  // Attraction filters
  const [bestTimeOfDay, setBestTimeOfDay] = useState<string[]>([]);
  const [bestSeason, setBestSeason] = useState<string[]>([]);
  const [difficulty, setDifficulty] = useState('any');
  const [crowdLevel, setCrowdLevel] = useState('any');
  const [typicalTimeSpent, setTypicalTimeSpent] = useState('any');
  const [selectedAttractionTags, setSelectedAttractionTags] = useState<string[]>([]);
  
  const [activeFiltersCount, setActiveFiltersCount] = useState(0);
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [showTagsModal, setShowTagsModal] = useState(false);

  // Subtype mappings for each type
  const subtypeMapping: Record<string, string[]> = {
    food: [
      'All',
      'Chinese restaurant',
      'Thai restaurant',
      'Asian restaurant',
      'Japanese restaurant',
      'Indian restaurant',
      'Fast food restaurant',
      'Seafood restaurant',
      'Italian restaurant',
      'Cafe',
      'Restaurant'
    ],
    experience: [
      'All',
      'Surfing',
      'Wildlife Safari',
      'Cooking class',
      'Tea tasting',
      'Adventure sports',
      'Whale watching',
      'Scuba & Snorkeling',
      'Boat tours',
      'Other'
    ],
    hotels: [
      'All',
      'Hostel',
      'Hotel',
      'Resort hotel'
    ],
    attractions: [
      'All',
      'Attraction',
      'Beach',
      'National park',
      'Buddhist temple',
      'Hiking area',
      'Museum',
      'Waterfall'
    ]
  };

  const cities = [
    'All Cities',
    'Colombo',
    'Kandy',
    'Ella',
    'Galle',
    'Negombo',
    'Nuwara Eliya',
    'Dambulla',
    'Mirissa',
    'Sigiriya',
    'Unawatuna',
    'Weligama',
    'Anuradhapura',
    'Trincomalee',
    'Bentota',
    'Polonnaruwa'
  ];
  
  const ratings = ['any', '4.0+', '4.3+', '4.6+', '4.8+'];
  const hotelStars = ['any', '3', '4', '5'];
  
  // Hotel tags organized by category
  const hotelViewTags = ['Ocean View', 'Sea View', 'City View', 'Beautiful Views', 'Stunning Views', 'Breathtaking Views'];
  const hotelAtmosphereTags = ['Peaceful Atmosphere', 'Relaxing Atmosphere', 'Calm Atmosphere', 'Serene Atmosphere', 'Cozy Atmosphere'];
  const hotelStyleTags = ['Luxury Hotel', 'Luxurious Stay', 'Colonial Charm', 'Historic Building', 'Modern Design', 'Unique Architecture'];
  const hotelGuestTypeTags = ['Family Friendly', 'Romantic Getaway', 'Wedding Venue', 'Event Venue', 'Nature Escape'];
  const hotelFeatureTags = ['Great Pool', 'Infinity Pool', 'Private Pool', 'Beautiful Beach', 'Beachfront Property', 'Spa Experience', 'Rooftop Bar', 'Beautiful Gardens'];
  const hotelServiceTags = ['Exceptional Service', 'Excellent Service', 'Great Service', 'Friendly Staff', 'Attentive Staff', 'Warm Hospitality'];
  const hotelRoomTags = ['Spacious Rooms', 'Comfortable Rooms', 'Clean Rooms', 'Luxurious Rooms'];
  
  const cuisines = [
    'Sri Lankan',
    'Seafood',
    'Fusion',
    'International',
    'Italian',
    'Western',
    'Indian',
    'Vegetarian',
    'Pizza',
    'Fast Food',
    'Vegan',
    'Middle Eastern',
    'Chinese',
    'Japanese'
  ];
  
  const signatureDishes = [
    'Rice and Curry',
    'Kottu',
    'Chicken Biryani',
    'Pizza',
    'Fried Rice',
    'Seafood Platter',
    'Burgers',
    'Chicken Curry',
    'Pasta',
    'Hot Butter Cuttlefish',
    'Crepes',
    'Grilled Prawns',
    'Smoothie Bowl',
    'Avocado Toast',
    'Pancakes',
    'Hoppers'
  ];
  
  // Food tags organized by category
  const foodAtmosphereTags = ['Cozy Atmosphere', 'Casual Dining', 'Relaxing Atmosphere', 'Elegant Atmosphere', 'Vibrant Atmosphere', 'Live Music'];
  const foodServiceTags = ['Great Service', 'Friendly Staff', 'Exceptional Service', 'Quick Service'];
  const foodExperienceTags = ['Family Friendly', 'Beachfront Dining', 'Rooftop Dining', 'Outdoor Seating', 'Buffet', 'Event Venue'];
  const foodQualityTags = ['Delicious Food', 'Fresh Ingredients', 'Authentic Flavors', 'Generous Portions', 'Great Value'];
  const foodSpecialtyTags = ['Vegan Options', 'Vegetarian Options', 'Halal Options', 'Healthy Food', 'Local Cuisine', 'Wood-Fired'];
  const foodDrinkTags = ['Great Coffee', 'Great Cocktails', 'Desserts'];
  
  const timesOfDay = ['Sunrise', 'Morning', 'Afternoon', 'Sunset', 'Evening', 'Night'];
  const seasons = ['Jan-Mar', 'Apr-Jun', 'Jul-Sep', 'Oct-Dec', 'Year Round'];
  const difficulties = ['any', 'Easy', 'Medium', 'Hard'];
  const crowdLevels = ['any', 'Quiet', 'Moderate', 'Crowded'];
  const timeSpent = ['any', 'Quick Stop', '2-3 Hours', 'Half Day Trip', 'Full Day Trip'];
  
  // Attraction tags organized by category
  const attractionTypeTags = ['Buddhist Temple', 'Hindu Temple', 'Waterfall', 'Beach', 'Botanical Garden', 'Historical Site', 'Wildlife Sanctuary', 'National Park', 'Ancient Ruins'];
  const attractionExperienceTags = ['Nature Lovers', 'Hiking', 'Photography Spot', 'Spiritual Experience', 'Cultural Heritage', 'Family Friendly', 'Adventure'];
  const attractionFeatureTags = ['UNESCO Site', 'Sunset Views', 'Ocean Views', 'Breathtaking Views', 'Scenic Views', 'Panoramic Views', 'Natural Pools'];
  const attractionAtmosphereTags = ['Peaceful Atmosphere', 'Serene Atmosphere', 'Calm Atmosphere', 'Hidden Gem', 'Tranquil Retreat'];
  const attractionActivityTags = ['Safari Experience', 'Wildlife Watching', 'Bird Watching', 'Climbing', 'Swimming', 'Camping', 'Meditation'];

  const toggleSelection = (item: string, list: string[], setList: (list: string[]) => void) => {
    if (list.includes(item)) {
      setList(list.filter(i => i !== item));
    } else {
      setList([...list, item]);
    }
  };

  const clearFilters = () => {
    setSelectedType('all');
    setSelectedSubtype('all');
    setMinRating('any');
    setSelectedCity('all');
    
    // Hotel
    setHotelStar('any');
    setSelectedTags([]);
    setHotelPriceRange([0, 500]);
    
    // Food
    setSelectedCuisines([]);
    setSelectedSignatureDish([]);
    setSelectedFoodTags([]);
    setFoodPriceRange([0, 10000]);
    
    // Attraction
    setBestTimeOfDay([]);
    setBestSeason([]);
    setDifficulty('any');
    setCrowdLevel('any');
    setTypicalTimeSpent('any');
    setSelectedAttractionTags([]);
    
    setActiveFiltersCount(0);
  };

  const applyFilters = () => {
    let count = 0;
    if (selectedType !== 'all') count++;
    if (selectedSubtype !== 'all') count++;
    if (minRating !== 'any') count++;
    if (selectedCity !== 'all') count++;
    
    if (selectedType === 'hotels') {
      if (hotelStar !== 'any') count++;
      if (selectedTags.length > 0) count++;
      if (hotelPriceRange[0] > 0 || hotelPriceRange[1] < 500) count++;
    }
    
    if (selectedType === 'food') {
      if (selectedCuisines.length > 0) count++;
      if (selectedSignatureDish.length > 0) count++;
      if (selectedFoodTags.length > 0) count++;
      if (foodPriceRange[0] > 0 || foodPriceRange[1] < 10000) count++;
    }
    
    if (selectedType === 'attractions') {
      if (bestTimeOfDay.length > 0) count++;
      if (bestSeason.length > 0) count++;
      if (difficulty !== 'any') count++;
      if (crowdLevel !== 'any') count++;
      if (typicalTimeSpent !== 'any') count++;
      if (selectedAttractionTags.length > 0) count++;
    }
    
    if (selectedType === 'experience') {
      if (bestTimeOfDay.length > 0) count++;
      if (bestSeason.length > 0) count++;
      if (difficulty !== 'any') count++;
      if (typicalTimeSpent !== 'any') count++;
    }
    
    setActiveFiltersCount(count);
    
    // Create filter state object
    const filterState: FilterState = {
      selectedType,
      selectedSubtype,
      minRating,
      selectedCity,
      hotelStar,
      selectedTags,
      hotelPriceRange,
      selectedCuisines,
      selectedSignatureDish,
      selectedFoodTags,
      foodPriceRange,
      bestTimeOfDay,
      bestSeason,
      difficulty,
      crowdLevel,
      typicalTimeSpent,
      selectedAttractionTags
    };
    
    onFiltersChange(filterState);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-end">
      <div className="bg-white w-full rounded-t-3xl max-h-[85vh] overflow-hidden flex flex-col animate-slide-up">
        {/* Modal Header */}
        <div className="p-4 border-b flex items-center justify-between sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-6">
          
          {/* Type Selection */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Type</h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {['all', 'hotels', 'food', 'attractions', 'experience'].map((type) => (
                <button
                  key={type}
                  onClick={() => {
                    setSelectedType(type);
                    setSelectedSubtype('all'); // Reset subtype when type changes
                  }}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                    selectedType === type
                      ? 'bg-gray-900 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </button>
              ))}
            </div>
          </div>
          
          {/* Subtype Selection - Show only when a specific type is selected */}
          {selectedType !== 'all' && subtypeMapping[selectedType] && (
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Subtype</h3>
              <div className="grid grid-cols-2 gap-2">
                {subtypeMapping[selectedType].map((subtype) => (
                  <button
                    key={subtype}
                    onClick={() => setSelectedSubtype(subtype === 'All' ? 'all' : subtype)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      selectedSubtype === (subtype === 'All' ? 'all' : subtype)
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {subtype}
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {/* City Selection - Common for all */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Location</h3>
            <div className="grid grid-cols-2 gap-2">
              {cities.map((city) => {
                const cityValue = city === 'All Cities' ? 'all' : city;
                return (
                  <button
                    key={city}
                    onClick={() => setSelectedCity(cityValue)}
                    className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                      selectedCity === cityValue
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                  >
                    {city}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Rating - Common for all */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3">Minimum Rating</h3>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {ratings.map((rating) => (
                <button
                  key={rating}
                  onClick={() => setMinRating(rating)}
                  className={`px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                    minRating === rating
                      ? 'bg-gray-800 text-white'
                      : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  {rating === 'any' ? 'Any' : `${rating}⭐`}
                </button>
              ))}
            </div>
          </div>

          {/* HOTEL SPECIFIC FILTERS */}
          {selectedType === 'hotels' && (
            <>
              {/* Hotel Star Rating */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Hotel Star</h3>
                <div className="flex gap-2">
                  {hotelStars.map((star) => (
                    <button
                      key={star}
                      onClick={() => setHotelStar(star)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        hotelStar === star
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {star === 'any' ? 'Any' : `${star}⭐`}
                    </button>
                  ))}
                </div>
              </div>

              {/* Hotel Tags - Organized by Categories */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Tags</h3>
                
                {/* Views */}
                <div className="mb-4">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === 'views' ? null : 'views')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 mb-2"
                  >
                    <span>🏞️ Views & Scenery</span>
                    <ChevronRight size={16} className={`transition-transform ${expandedCategory === 'views' ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedCategory === 'views' && (
                    <div className="flex flex-wrap gap-2 pl-2">
                      {hotelViewTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => toggleSelection(tag, selectedTags, setSelectedTags)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            selectedTags.includes(tag)
                              ? 'bg-gray-700 text-white'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {selectedTags.includes(tag) && <Check size={12} className="inline mr-1" />}
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Atmosphere */}
                <div className="mb-4">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === 'atmosphere' ? null : 'atmosphere')}
                    className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 mb-2"
                  >
                    <span>✨ Atmosphere</span>
                    <ChevronRight size={16} className={`transition-transform ${expandedCategory === 'atmosphere' ? 'rotate-90' : ''}`} />
                  </button>
                  {expandedCategory === 'atmosphere' && (
                    <div className="flex flex-wrap gap-2 pl-2">
                      {hotelAtmosphereTags.map((tag) => (
                        <button
                          key={tag}
                          onClick={() => toggleSelection(tag, selectedTags, setSelectedTags)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                            selectedTags.includes(tag)
                              ? 'bg-gray-800 text-white'
                              : 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {selectedTags.includes(tag) && <Check size={12} className="inline mr-1" />}
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Selected Tags Summary */}
                {selectedTags.length > 0 && (
                  <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-900">
                        {selectedTags.length} tag{selectedTags.length > 1 ? 's' : ''} selected
                      </span>
                      <button
                        onClick={() => setSelectedTags([])}
                        className="text-xs text-gray-600 hover:text-gray-800"
                      >
                        Clear all
                      </button>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {selectedTags.map((tag) => (
                        <span key={tag} className="text-xs bg-gray-200 text-gray-800 px-2 py-1 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Hotel Price Range */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900">Price per Night</h3>
                  <span className="text-sm text-gray-600 font-medium">
                    ${hotelPriceRange[0]} - ${hotelPriceRange[1]}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="relative pt-1 pb-2">
                    {/* Track background */}
                    <div className="absolute w-full h-2 bg-gray-200 rounded-lg top-1/2 -translate-y-1/2"></div>
                    {/* Active range */}
                    <div 
                      className="absolute h-2 bg-gray-700 rounded-lg top-1/2 -translate-y-1/2"
                      style={{
                        left: '0%',
                        width: `${(hotelPriceRange[1] / 500) * 100}%`
                      }}
                    />
                    {/* Slider */}
                    <input
                      type="range"
                      min="0"
                      max="500"
                      step="10"
                      value={hotelPriceRange[1]}
                      onChange={(e) => setHotelPriceRange([0, parseInt(e.target.value)])}
                      className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                      style={{
                        zIndex: 3,
                        height: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>$0</span>
                    <span>$250</span>
                    <span>$500+</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* FOOD SPECIFIC FILTERS */}
          {selectedType === 'food' && (
            <>
              {/* Signature Dishes */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Signature Dishes</h3>
                <div className="flex flex-wrap gap-2">
                  {signatureDishes.map((dish) => (
                    <button
                      key={dish}
                      onClick={() => toggleSelection(dish, selectedSignatureDish, setSelectedSignatureDish)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedSignatureDish.includes(dish)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {selectedSignatureDish.includes(dish) && <Check size={14} className="inline mr-1" />}
                      {dish}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cuisine */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Cuisine</h3>
                <div className="flex flex-wrap gap-2">
                  {cuisines.map((cuisine) => (
                    <button
                      key={cuisine}
                      onClick={() => toggleSelection(cuisine, selectedCuisines, setSelectedCuisines)}
                      className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                        selectedCuisines.includes(cuisine)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {selectedCuisines.includes(cuisine) && <Check size={14} className="inline mr-1" />}
                      {cuisine}
                    </button>
                  ))}
                </div>
              </div>

              {/* Food Price Range */}
              <div>
                <div className="flex justify-between items-center mb-3">
                  <h3 className="font-semibold text-gray-900">Price per Person</h3>
                  <span className="text-sm text-gray-600 font-medium">
                    Rs {foodPriceRange[0].toLocaleString()} - Rs {foodPriceRange[1].toLocaleString()}
                  </span>
                </div>
                <div className="space-y-2">
                  <div className="relative pt-1 pb-2">
                    {/* Track background */}
                    <div className="absolute w-full h-2 bg-gray-200 rounded-lg top-1/2 -translate-y-1/2"></div>
                    {/* Active range */}
                    <div 
                      className="absolute h-2 bg-gray-700 rounded-lg top-1/2 -translate-y-1/2"
                      style={{
                        left: `${(foodPriceRange[0] / 10000) * 100}%`,
                        width: `${((foodPriceRange[1] - foodPriceRange[0]) / 10000) * 100}%`
                      }}
                    />
                    {/* Min slider */}
                    <input
                      type="range"
                      min="0"
                      max="10000"
                      step="500"
                      value={foodPriceRange[0]}
                      onChange={(e) => {
                        const newMin = parseInt(e.target.value);
                        if (newMin <= foodPriceRange[1] - 500) {
                          setFoodPriceRange([newMin, foodPriceRange[1]]);
                        }
                      }}
                      className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                      style={{
                        zIndex: 3,
                        height: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    />
                    {/* Max slider */}
                    <input
                      type="range"
                      min="0"
                      max="10000"
                      step="500"
                      value={foodPriceRange[1]}
                      onChange={(e) => {
                        const newMax = parseInt(e.target.value);
                        if (newMax >= foodPriceRange[0] + 500) {
                          setFoodPriceRange([foodPriceRange[0], newMax]);
                        }
                      }}
                      className="absolute w-full appearance-none bg-transparent pointer-events-auto cursor-pointer"
                      style={{
                        zIndex: 4,
                        height: '28px',
                        top: '50%',
                        transform: 'translateY(-50%)'
                      }}
                    />
                  </div>
                  <div className="flex justify-between text-xs text-gray-500">
                    <span>Rs 0</span>
                    <span>Rs 5,000</span>
                    <span>Rs 10,000+</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* ATTRACTION SPECIFIC FILTERS */}
          {selectedType === 'attractions' && (
            <>
              {/* Best Time of Day */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Best Time of Day</h3>
                <div className="grid grid-cols-3 gap-2">
                  {timesOfDay.map((time) => (
                    <button
                      key={time}
                      onClick={() => toggleSelection(time, bestTimeOfDay, setBestTimeOfDay)}
                      className={`py-2.5 rounded-lg text-xs font-medium transition-all ${
                        bestTimeOfDay.includes(time)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {bestTimeOfDay.includes(time) && <Check size={12} className="inline mr-1" />}
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Best Season */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Best Season</h3>
                <div className="grid grid-cols-2 gap-2">
                  {seasons.map((season) => (
                    <button
                      key={season}
                      onClick={() => toggleSelection(season, bestSeason, setBestSeason)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        bestSeason.includes(season)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {bestSeason.includes(season) && <Check size={14} className="inline mr-1" />}
                      {season}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Difficulty</h3>
                <div className="flex gap-2">
                  {difficulties.map((level) => (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        difficulty === level
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Crowd Level */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Crowd Level</h3>
                <div className="flex gap-2">
                  {crowdLevels.map((level) => (
                    <button
                      key={level}
                      onClick={() => setCrowdLevel(level)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        crowdLevel === level
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Typical Time Spent */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Typical Time Spent</h3>
                <div className="grid grid-cols-2 gap-2">
                  {timeSpent.map((time) => (
                    <button
                      key={time}
                      onClick={() => setTypicalTimeSpent(time)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        typicalTimeSpent === time
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* EXPERIENCE SPECIFIC FILTERS */}
          {selectedType === 'experience' && (
            <>
              {/* Best Time of Day */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Best Time of Day</h3>
                <div className="grid grid-cols-3 gap-2">
                  {timesOfDay.map((time) => (
                    <button
                      key={time}
                      onClick={() => toggleSelection(time, bestTimeOfDay, setBestTimeOfDay)}
                      className={`py-2.5 rounded-lg text-xs font-medium transition-all ${
                        bestTimeOfDay.includes(time)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {bestTimeOfDay.includes(time) && <Check size={12} className="inline mr-1" />}
                      {time}
                    </button>
                  ))}
                </div>
              </div>

              {/* Best Season */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Best Season</h3>
                <div className="grid grid-cols-2 gap-2">
                  {seasons.map((season) => (
                    <button
                      key={season}
                      onClick={() => toggleSelection(season, bestSeason, setBestSeason)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        bestSeason.includes(season)
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {bestSeason.includes(season) && <Check size={14} className="inline mr-1" />}
                      {season}
                    </button>
                  ))}
                </div>
              </div>

              {/* Difficulty */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Difficulty</h3>
                <div className="flex gap-2">
                  {difficulties.map((level) => (
                    <button
                      key={level}
                      onClick={() => setDifficulty(level)}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        difficulty === level
                          ? 'bg-gray-800 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {level.charAt(0).toUpperCase() + level.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Typical Time Spent */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Typical Time Spent</h3>
                <div className="grid grid-cols-2 gap-2">
                  {timeSpent.map((time) => (
                    <button
                      key={time}
                      onClick={() => setTypicalTimeSpent(time)}
                      className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                        typicalTimeSpent === time
                          ? 'bg-gray-700 text-white'
                          : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {time}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}

        </div>

        {/* Apply Button */}
        <div className="p-4 border-t bg-white sticky bottom-0">
          <button
            onClick={applyFilters}
            className="w-full bg-gray-900 text-white py-4 rounded-xl font-semibold text-lg hover:bg-gray-800 transition-colors shadow-lg"
          >
            Apply Filters
          </button>
        </div>
      </div>

    </div>
  );
};

export default TravelFilterUI;
